/**
 * Lightweight date validators shared by every form that ships a date
 * input. The HTML5 `<input type="date">` already rejects mistyped
 * characters at the OS level, but the live API has historically
 * surfaced corrupt rows like "0020-06-20" — a 4-digit year of just
 * "20", which Date.parse cheerfully treats as 20 AD. These helpers
 * give us a consistent guard for the obvious failure modes.
 */

/**
 * Year cutoff for sanity. Anything before this is presumed corrupt
 * data (e.g. "0020"), and anything 100 years past today is presumed
 * a future-form typo.
 */
const MIN_YEAR = 1900;
const MAX_YEAR = new Date().getFullYear() + 100;

/**
 * Strict ISO-date check. Accepts "YYYY-MM-DD" or full ISO timestamps,
 * rejects everything else including the empty string. Used by grid
 * renderers and form-level validators alike.
 */
export function isValidIsoDate(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') return false;
  // Match either YYYY-MM-DD or full ISO. The HTML5 date input always
  // emits the bare YYYY-MM-DD form.
  if (!/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value)) return false;
  const t = Date.parse(value);
  if (Number.isNaN(t)) return false;
  const d = new Date(t);
  const y = d.getFullYear();
  if (y < MIN_YEAR || y > MAX_YEAR) return false;
  return true;
}

/**
 * Returns null if the date is valid, else a short reason. Suitable
 * to pass directly to a form's `validate` callback.
 *
 *   const e = validateDate(form.startDate, 'Start date');
 *   if (e) return e;
 */
export function validateDate(
  value: string | null | undefined,
  fieldLabel: string,
): string | null {
  if (!value) return null; // optional date — let the required-validator handle empty
  if (!isValidIsoDate(value)) {
    return `${fieldLabel} is not a valid date — re-enter as YYYY-MM-DD`;
  }
  return null;
}

/**
 * Range validator: end must be >= start. Both must be valid ISO dates
 * for the comparison to run; if either is empty the check is skipped
 * so callers can decide whether emptiness is allowed.
 *
 *   const e = validateDateRange(form.startDate, form.endDate, 'Start', 'End');
 *   if (e) return e;
 */
export function validateDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
  startLabel = 'Start date',
  endLabel = 'End date',
): string | null {
  if (!start || !end) return null;
  const startErr = validateDate(start, startLabel);
  if (startErr) return startErr;
  const endErr = validateDate(end, endLabel);
  if (endErr) return endErr;
  if (Date.parse(end) < Date.parse(start)) {
    return `${endLabel} must be on or after ${startLabel.toLowerCase()}`;
  }
  return null;
}
